import { useEffect } from 'react';
import { shallow } from 'zustand/shallow';
import { useDynamicEntityCMSDataContext, SitemapStoreProvider, useSitemapStoreContext } from './contexts';
import { SITEMAP_LOCAL_STORAGE_KEY, SOURCE_LEGEND, STATUS_LEGEND } from './sitemap_lib';

import TextInput from '../inputs/form-inputs/TextInput';
import SiteTree from './_builder_sitemap/SiteTree';
import SiteList from './_builder_sitemap/SiteList';
import PulsatingBadge from './_builder_sitemap/PulsatingBadge';
import SitemapToolbar from './_builder_sitemap/SitemapToolbar';

function BuilderSitemapContent(props) {
  const { isSimple } = props;
  const { allSitePages } = useDynamicEntityCMSDataContext();

  const isLoading = useSitemapStoreContext((state) => state.isLoading);
  const view = useSitemapStoreContext((state) => state.view, shallow);
  const sort = useSitemapStoreContext((state) => state.sort, shallow);
  const hide = useSitemapStoreContext((state) => state.hide, shallow);
  const expandedKeys = useSitemapStoreContext((state) => state.expandedKeys, shallow);
  const statusCount = useSitemapStoreContext((state) => state.statusCount, shallow);
  const update = useSitemapStoreContext((state) => state.update);

  const handleSearch = (e) => update({ search: e.target.value });
  const handleToggleHide = (key) => update({ [`hide.${key}`]: !hide[key] });

  useEffect(() => {
    localStorage.setItem(SITEMAP_LOCAL_STORAGE_KEY, JSON.stringify({ view, hide, sort, expandedKeys }));
  }, [view, hide, sort, expandedKeys]);

  return (
    <div className="dp-site-tree">
      <div className="d-flex flex-column gap-3">
        {isSimple ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-3">
            <TextInput label="Search" placeholder={`Search all ${allSitePages.length} items by title or URL...`} onChange={handleSearch} />
            <SitemapToolbar />
          </div>
        ) : (
          <div className="d-flex gap-3">
            <TextInput label="Search" placeholder={`Search all ${allSitePages.length} items by title or URL...`} onChange={handleSearch} />
            <SitemapToolbar />
          </div>
        )}

        {isSimple ? (
          <div className="w-100 d-flex gap-2 align-items-center justify-content-center">
            {Object.entries(STATUS_LEGEND).map(([key, { label, variant }]) => (
              <PulsatingBadge
                withBorder
                key={key}
                badgeLabel={`${label}: ${statusCount[key] || 0}`}
                variant={variant}
                onClick={() => handleToggleHide(key)}
                isHide={hide[key]}
              />
            ))}
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-light d-flex gap-1" data-bs-toggle="dropdown" aria-expanded="false">
                Summary <i className="fe fe-bar-chart-2" />
              </button>
              <div className="dropdown-menu p-3">
                <ul className="m-0 list-unstyled">
                  {Object.entries(SOURCE_LEGEND).map(([key, { label }]) => (
                    <li key={key}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="badge">{label}</span>
                        {statusCount[key] || 0}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="d-flex gap-2 align-items-center">
              {Object.entries(STATUS_LEGEND).map(([key, { label, variant }]) => (
                <PulsatingBadge
                  withBorder
                  key={key}
                  badgeLabel={`${label}: ${statusCount[key] || 0}`}
                  variant={variant}
                  onClick={() => handleToggleHide(key)}
                  isHide={hide[key]}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="dp-site-tree-list loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {view === 0 && <SiteTree isSimple={isSimple} />}
          {view === 1 && <SiteList isSimple={isSimple} />}
        </>
      )}
    </div>
  );
}

export default function BuilderSitemap(props) {
  const { allSitePages, categoriesWithTemplates } = useDynamicEntityCMSDataContext();
  return (
    <SitemapStoreProvider allSitePages={allSitePages} categoriesWithTemplates={categoriesWithTemplates}>
      <BuilderSitemapContent {...props} />
    </SitemapStoreProvider>
  );
}
